import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Book, ChevronLeft, ChevronRight, Flag, Plus } from 'lucide-react'

import NormalTopBar from '@/components/common/bar/NormalTopBar'
import CardOne from '@/components/common/card/CardOne'
import { useGetDailyMissionQuery } from '@/hooks/queries/dailyMission'
import { useCompleteToggleMutation } from '@/hooks/queries/dailyMission/mutation'
import PullToRefresh from '@/components/common/refresh/PullToRefresh'
import type { TypeDailyMissionItem } from '@/api/model/dailyMissionType'

const MissionPage = () => {
  const navigate = useNavigate()
  const [currentWeek, setCurrentWeek] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date())
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  // 현재 사용자 ID (실제로는 로그인 상태에서 가져와야 함)
  const { data: dailyMission } = useGetDailyMissionQuery(selectedDate?.toISOString().split('T')[0] || '')

  const queryClient = useQueryClient()

  const { mutate: completeToggle } = useCompleteToggleMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyMission'] })
      queryClient.invalidateQueries({ queryKey: ['dailyMissionDateRange'] })
      queryClient.invalidateQueries({ queryKey: ['missionCalendar'] })
    }
  })

  // 주의 시작일 (월요일) 계산
  const getWeekStart = (date: Date) => {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1) // 월요일 기준
    return new Date(d.setDate(diff))
  }

  // 현재 주의 날짜들 생성
  const getWeekDays = () => {
    const weekStart = getWeekStart(currentWeek)
    const days = []

    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart)
      day.setDate(weekStart.getDate() + i)
      days.push(day)
    }
    return days
  }

  const formatWeekRange = (days: Date[]) => {
    if (days.length === 0) {
      return ''
    }

    const firstDay = days[0]
    const lastDay = days[days.length - 1]

    const firstYear = firstDay.getFullYear()
    const firstMonth = firstDay.getMonth() + 1
    const lastYear = lastDay.getFullYear()
    const lastMonth = lastDay.getMonth() + 1

    if (firstYear === lastYear) {
      if (firstMonth === lastMonth) {
        return `${firstYear}년 ${firstMonth}월`
      }

      return `${firstYear}년 ${firstMonth}월 ~ ${lastMonth}월`
    }

    return `${firstYear}년 ${firstMonth}월 ~ ${lastYear}년 ${lastMonth}월`
  }

  // 이전 주로 이동
  const goToPreviousWeek = () => {
    const newWeek = new Date(currentWeek)
    newWeek.setDate(currentWeek.getDate() - 7)
    setCurrentWeek(newWeek)
  }

  // 다음 주로 이동
  const goToNextWeek = () => {
    const newWeek = new Date(currentWeek)
    newWeek.setDate(currentWeek.getDate() + 7)
    setCurrentWeek(newWeek)
  }

  // 터치 드래그 최소 거리
  const minSwipeDistance = 50

  // 터치 시작
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  // 터치 이동
  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  // 터치 종료
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      goToNextWeek() // 왼쪽으로 스와이프 → 다음 주
    } else if (isRightSwipe) {
      goToPreviousWeek() // 오른쪽으로 스와이프 → 이전 주
    }
  }

  const weekDays = getWeekDays()
  const today = new Date()

  // 요일 이름
  const dayNames = ['월', '화', '수', '목', '금', '토', '일']

  // 오늘로 이동
  const goToToday = () => {
    setCurrentWeek(new Date())
  }

  // 빈 미션 목록 메시지 결정
  const getEmptyMessage = () => {
    if (!selectedDate) {
      return '오늘 예정된 미션이 없습니다.'
    }

    const isToday = selectedDate.toDateString() === today.toDateString()
    if (isToday) {
      return '오늘 예정된 미션이 없습니다.'
    }

    const month = selectedDate.getMonth() + 1
    const date = selectedDate.getDate()
    return `${month}월 ${date}일에 예정된 미션이 없습니다.`
  }

  const handleCompleteToggle = (daily_mission_id: string) => {
    completeToggle({ daily_mission_id })
  }

  return (
    <div className='h-full flex flex-col'>
      <NormalTopBar
        textClassName='text-xl text-text-header font-semibold'
        boxClassName='sticky top-0 z-10 bg-gradient-to-r from-amber-400 to-yellow-300'
        title='미션 관리'
        rightSide={
          <div className='flex items-center gap-1'>
            <button
              className='w-9 h-9 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors'
              onClick={() => navigate('/mission/create/new')}
            >
              <Plus className='w-5 h-5 text-text-header' />
            </button>
            <button
              className='w-9 h-9 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors'
              onClick={() => navigate('/mission/all')}
            >
              <Book className='w-5 h-5 text-text-header' />
            </button>
          </div>
        }
      />
      <PullToRefresh
        onRefresh={() => {
          window.location.reload()
        }}
      >
        <div className='h-full p-3 overflow-y-hidden flex flex-col gap-3 pb-23'>
          <CardOne className='px-3 py-5'>
            {/* 네비게이션 */}
            <div className='flex items-center justify-between mb-3'>
              <button onClick={goToPreviousWeek} className='p-1 hover:bg-amber-100 rounded-full transition-colors'>
                <ChevronLeft size={20} className='text-sm text-amber-900' />
              </button>

              <button
                onClick={goToToday}
                className='font-bold text-sm text-gray-900 hover:text-gray-700 transition-colors'
              >
                {formatWeekRange(weekDays)}
              </button>

              <button onClick={goToNextWeek} className='p-1 hover:bg-amber-100 rounded-full transition-colors'>
                <ChevronRight size={20} className='text-sm text-amber-900' />
              </button>
            </div>
            {/* 요일 헤더 */}
            <div className='grid grid-cols-7 gap-1 mb-2'>
              {dayNames.map(day => (
                <div key={day} className='text-center text-sm text-gray-700 font-medium py-2'>
                  {day}
                </div>
              ))}
            </div>

            {/* 날짜 그리드 */}
            <div
              className='grid grid-cols-7 gap-1 mb-4 select-none'
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {weekDays.map((date, index) => {
                const isToday = date.toDateString() === today.toDateString()
                const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString()

                return (
                  <div key={index} onClick={() => setSelectedDate(date)} className='flex flex-col items-center'>
                    <div
                      className={`
                  w-9 h-9 flex flex-col items-center justify-center p-1 rounded-full cursor-pointer transition-colors
                  ${
                    isSelected
                      ? 'bg-amber-500 text-white font-bold'
                      : isToday
                        ? 'bg-amber-200 text-gray-900 font-bold'
                        : 'hover:bg-amber-100 text-gray-700'
                  }
                `}
                    >
                      <span className='text-sm'>{date.getDate()}</span>
                    </div>
                  </div>
                )
              })}
            </div>
            <hr className='border-amber-200 mb-4' />
            {/* 오늘의 미션 목록 */}
            <div>
              {dailyMission?.data.missions?.filter((mission: TypeDailyMissionItem) => {
                return !mission.is_stored
              }).length === 0 ? (
                <div className='text-center py-8 text-gray-500'>
                  <p>{getEmptyMessage()}</p>
                </div>
              ) : (
                dailyMission?.data.missions
                  ?.filter((mission: TypeDailyMissionItem) => {
                    return !mission.is_stored
                  })
                  .map((mission: TypeDailyMissionItem) => (
                    <div
                      key={mission.mission_id}
                      className='flex items-center space-x-3 p-3 rounded-xl hover:bg-amber-50 transition-colors'
                    >
                      <Flag className={`w-5 h-5 ${mission.is_completed ? 'text-gray-400' : 'text-amber-500'}`} />
                      <span
                        className={`flex-1 font-medium text-sm ${mission.is_completed ? 'line-through text-gray-400' : 'text-gray-700'}`}
                        role='button'
                        onClick={() => navigate(`/mission/detail/${mission.mission_id}`)}
                      >
                        {mission.name}
                      </span>
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors cursor-pointer ${
                          mission.is_completed
                            ? 'bg-gray-400 border-gray-400 text-white'
                            : 'border-amber-500 hover:border-amber-500'
                        }`}
                        onClick={() => {
                          handleCompleteToggle(mission.daily_mission_id?.toString() ?? '')
                        }}
                      >
                        {mission.is_completed && <span className='text-xs text-white'>✓</span>}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </CardOne>
        </div>
      </PullToRefresh>
    </div>
  )
}

export default MissionPage
